import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Package, MapPin, Building2, ArrowLeft, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useSession } from "../context/SessionContext";
import { ForgotPasswordModal } from "../components/ForgotPasswordModal";
import { toast } from "sonner";

export default function ClientLogin() {
  const [nit, setNit] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [multiSede, setMultiSede] = useState(null); // { nit, razon_social, sedes[] }
  const [selectingId, setSelectingId] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { loginNit, selectSede } = useSession();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nit.trim()) { setError("Ingresa tu NIT"); return; }
    if (!password.trim()) { setError("Ingresa la contraseña que te compartió CM"); return; }
    setLoading(true);
    try {
      const result = await loginNit(nit.trim(), password.trim());
      if (result?.multi_sede) {
        setMultiSede(result);
        return;
      }
      toast.success(`Bienvenido, ${result.razon_social}`);
      navigate("/catalogo");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No pudimos validar tu acceso";
      setError(msg);
    } finally { setLoading(false); }
  };

  const onPickSede = async (sede) => {
    setSelectingId(sede.cliente_id);
    try {
      const c = await selectSede(multiSede.nit, sede.cliente_id, password.trim());
      toast.success(`Sede seleccionada: ${c.almacen}`);
      navigate("/catalogo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos cargar esta sede");
    } finally {
      setSelectingId(null);
    }
  };

  const onBackToNit = () => {
    setMultiSede(null);
    setError("");
  };

  return (
    <div className="min-h-screen bg-cm-cream flex flex-col lg:flex-row" data-testid="client-login-page">
      {/* Left hero */}
      <div className="lg:w-[55%] relative overflow-hidden bg-cm-ink text-cm-cream">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/bd543d09-1af2-4218-af84-8180b654732b/images/451fc3976ceb3a424b2df03522ff65d6615a3814470c2b720fa846dd0142222b.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cm-ink/80 via-cm-ink/60 to-transparent" />

        <div className="relative z-10 flex flex-col h-full p-8 sm:p-12 lg:p-16 min-h-[40vh] lg:min-h-screen">
          <Link to="/" className="flex items-center gap-2.5 fade-up fade-up-1">
            <img src="/cm-logo.jpeg" alt="CM Calidad y Servicio" className="h-12 w-12 rounded-full object-cover ring-2 ring-cm-yellow/60" />
            <div>
              <div className="font-serif text-base leading-tight">CM Calidad y Servicio</div>
              <div className="text-[10px] uppercase tracking-widest text-cm-yellow">Portal B2B</div>
            </div>
          </Link>

          <div className="mt-auto pt-12">
            <p className="text-cm-yellow font-mono text-xs uppercase tracking-widest mb-4 fade-up fade-up-1">
              Las mejores marcas del país
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight fade-up fade-up-2">
              Tus pedidos al<br/>
              <span className="text-cm-yellow italic">mejor precio</span><br/>
              del mercado.
            </h1>
            <p className="mt-6 max-w-md text-cm-cream/80 leading-relaxed fade-up fade-up-3">
              Plataforma exclusiva para clientes B2B de CM Calidad y Servicio.
              Catálogo completo con precios personalizados según tu lista comercial.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg fade-up fade-up-3">
              <div className="border-l-2 border-cm-yellow pl-3">
                <Truck className="h-5 w-5 text-cm-yellow mb-1.5" />
                <p className="text-xs leading-tight">Despachos<br/>en Cartagena</p>
              </div>
              <div className="border-l-2 border-cm-yellow pl-3">
                <Package className="h-5 w-5 text-cm-yellow mb-1.5" />
                <p className="text-xs leading-tight">Catálogo<br/>1000+ productos</p>
              </div>
              <div className="border-l-2 border-cm-yellow pl-3">
                <ShieldCheck className="h-5 w-5 text-cm-yellow mb-1.5" />
                <p className="text-xs leading-tight">Precios<br/>autorizados</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:w-[45%] flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8 fade-up fade-up-1">
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Acceso clientes</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">
              {multiSede ? "Selecciona tu sede" : "Ingresa con tu NIT"}
            </h2>
            <p className="text-cm-ink-soft mt-2 text-sm">
              {multiSede
                ? <>Encontramos <strong>{multiSede.sedes.length} sedes</strong> registradas para <strong>{multiSede.razon_social}</strong>. Elige la sede desde la que vas a pedir.</>
                : "Ingresa con tu NIT y la contraseña que te compartió CM."}
            </p>
          </div>

          {multiSede ? (
            <div className="space-y-2.5 fade-up fade-up-2" data-testid="sede-selector">
              {multiSede.sedes.map((s) => (
                <button
                  key={s.cliente_id}
                  type="button"
                  onClick={() => onPickSede(s)}
                  disabled={selectingId !== null}
                  data-testid={`sede-option-${s.cliente_id}`}
                  className="w-full text-left p-4 border border-cm-border rounded-lg hover:border-cm-red hover:bg-cm-red/5 transition-colors disabled:opacity-50 disabled:cursor-wait group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-cm-yellow/30 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-4 w-4 text-cm-ink" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-cm-ink truncate">{s.almacen}</p>
                      {s.direccion && (
                        <p className="text-xs text-cm-ink-soft mt-0.5 flex items-center gap-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{s.direccion}{s.ciudad ? ` · ${s.ciudad}` : ""}</span>
                        </p>
                      )}
                      <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft mt-1.5 font-mono">
                        Sede #{s.cliente_id}
                        {s.lista_precios_nombre ? ` · ${s.lista_precios_nombre}` : ""}
                      </p>
                    </div>
                    <div className="flex-shrink-0 self-center">
                      {selectingId === s.cliente_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-cm-red" />
                      ) : (
                        <ArrowRight className="h-4 w-4 text-cm-ink-soft group-hover:text-cm-red transition-colors" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={onBackToNit}
                disabled={selectingId !== null}
                className="text-cm-ink-soft hover:text-cm-ink mt-2"
                data-testid="back-to-nit-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Cambiar NIT
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5 fade-up fade-up-2">
            <div className="space-y-2">
              <Label htmlFor="nit-input" className="text-cm-ink font-medium">NIT del cliente</Label>
              <Input
                id="nit-input"
                placeholder="Ej: 900123456"
                value={nit}
                onChange={(e) => setNit(e.target.value)}
                className="h-12 text-base font-mono border-cm-border focus-visible:ring-cm-red focus-visible:ring-offset-0"
                data-testid="nit-input"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-input" className="text-cm-ink font-medium flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" /> Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Contraseña compartida por CM"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 text-base pr-10 border-cm-border focus-visible:ring-cm-red focus-visible:ring-offset-0"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cm-ink-soft hover:text-cm-ink"
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[11px] text-cm-ink-soft">
                ¿No la tienes? Solicítala por WhatsApp al{" "}
                <a
                  href="https://wa.me/573215006806?text=Hola%2C%20soy%20cliente%20de%20CM%20y%20necesito%20la%20contrase%C3%B1a%20del%20portal%20B2B."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cm-red hover:underline font-medium"
                  data-testid="request-password-link"
                >
                  321 5006 806
                </a>
              </p>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-[11px] text-cm-ink-soft hover:text-cm-red underline decoration-dotted underline-offset-2 self-start"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {error && (
              <div className="bg-cm-red/10 border border-cm-red/30 text-cm-red text-sm rounded-md px-3 py-2.5" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-cm-primary text-base"
              data-testid="nit-submit-btn"
            >
              {loading ? "Validando..." : <>Ingresar al portal <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
          )}

          <div className="mt-8 pt-6 border-t border-cm-border fade-up fade-up-3">
            <p className="text-xs text-cm-ink-soft mb-3">¿No tienes acceso?</p>
            <p className="text-sm text-cm-ink mb-2">Contáctanos por WhatsApp para registrar tu negocio:</p>
            <a
              href="https://wa.me/573215006806?text=Hola%2C%20quiero%20registrar%20mi%20negocio%20en%20el%20portal%20B2B%20de%20CM%20Calidad%20y%20Servicio."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors text-sm font-medium"
              data-testid="register-whatsapp-link"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              321 5006 806
            </a>
          </div>

          <div className="mt-8 text-center">
            <Link to="/admin/login" className="text-xs text-cm-ink-soft hover:text-cm-red transition-colors" data-testid="admin-portal-link">
              Soy del equipo CM →
            </Link>
          </div>
        </div>
      </div>
      <ForgotPasswordModal open={forgotOpen} onOpenChange={setForgotOpen} initialNit={nit} />
    </div>
  );
}
